<template>
  <form @submit.prevent="submitForm" class="promotion-form">
    <!-- ========================================== -->
    <!-- 1. عنوان جشنواره -->
    <!-- ========================================== -->
    <div class="form-group">
      <label class="required">عنوان جشنواره</label>
      <input
          v-model="form.title"
          type="text"
          placeholder="مثلاً: تخفیف ویژه مشتریان وفادار"
          class="form-control"
          maxlength="255"
          required
      />
      <small class="help-text">عنوانی که در پنل کاربری و گزارش‌ها نمایش داده می‌شود.</small>
    </div>

    <!-- ========================================== -->
    <!-- 2. نوع جشنواره -->
    <!-- ========================================== -->
    <div class="form-group">
      <label class="required">نوع جشنواره</label>
      <div class="radio-group">
        <label class="radio-label">
          <input type="radio" v-model="form.type" value="first_visit" />
          بار اول مراجعه
          <span class="radio-desc">(به کاربرانی که برای اولین بار مراجعه می‌کنند)</span>
        </label>
        <label class="radio-label">
          <input type="radio" v-model="form.type" value="loyalty_count" />
          وفاداری (تعداد مراجعه)
          <span class="radio-desc">(به کاربرانی که تعداد مشخصی مراجعه داشته باشند)</span>
        </label>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- 3. شرایط وفاداری (فقط برای نوع loyalty_count) -->
    <!-- ========================================== -->
    <template v-if="form.type === 'loyalty_count'">
      <div class="form-row">
        <div class="form-group half">
          <label class="required">تعداد مراجعه</label>
          <input
              v-model.number="form.min_visits"
              type="number"
              min="1"
              max="100"
              class="form-control"
              required
          />
          <small class="help-text">
            کاربر باید حداقل {{ form.min_visits || '...' }} بار مراجعه کند.
          </small>
        </div>

        <div class="form-group half">
          <label class="required">بازه زمانی (روز)</label>
          <input
              v-model.number="form.within_days"
              type="number"
              min="1"
              max="365"
              class="form-control"
              required
          />
          <small class="help-text">
            مراجعات باید در {{ form.within_days || '...' }} روز گذشته باشد.
          </small>
        </div>
      </div>

      <div class="info-box">
        <strong>📌 مثال:</strong>
        <span v-if="form.min_visits && form.within_days">
          کاربر باید حداقل {{ form.min_visits }} بار در {{ form.within_days }} روز گذشته مراجعه کند.
        </span>
        <span v-else>
          کاربر باید تعداد مشخصی بار در بازه زمانی مشخص مراجعه کند.
        </span>
      </div>
    </template>

    <!-- ========================================== -->
    <!-- 4. خدمات مشمول شرط (خدماتی که برای رسیدن به جایزه باید مراجعه کرد) -->
    <!-- ========================================== -->
    <div class="form-group">
      <label class="required">خدمات مشمول شرط</label>
      <p class="field-description">
        کاربر برای رسیدن به جایزه، باید به چه خدماتی مراجعه کند؟
      </p>
      <div class="radio-group">
        <label class="radio-label">
          <input type="radio" v-model="conditionType" value="all" />
          همه خدمات
          <span class="radio-desc">(کاربر با هر خدمتی به شرط می‌رسد)</span>
        </label>
        <label class="radio-label">
          <input type="radio" v-model="conditionType" value="specific" />
          فقط خدمات خاص
          <span class="radio-desc">(کاربر فقط با خدمات مشخص شده به شرط می‌رسد)</span>
        </label>
      </div>

      <!-- انتخاب خدمات شرط -->
      <div v-if="conditionType === 'specific'" class="checkbox-group">
        <label
            v-for="service in services"
            :key="service.id"
            class="checkbox-label"
        >
          <input
              type="checkbox"
              :value="service.id"
              v-model="form.condition_service_ids"
          />
          {{ service.title }}
        </label>
        <div v-if="form.condition_service_ids.length === 0" class="warning-text">
          ⚠️ لطفاً حداقل یک خدمت را انتخاب کنید.
        </div>
      </div>

      <small class="help-text">
        <span v-if="conditionType === 'all'">
          ✅ کاربر با مراجعه به هر خدمتی، مشمول این جشنواره می‌شود.
        </span>
        <span v-else>
          ✅ کاربر فقط با مراجعه به خدماتی که انتخاب می‌کنید، مشمول این جشنواره می‌شود.
        </span>
      </small>
    </div>

    <!-- ========================================== -->
    <!-- 5. نوع جایزه -->
    <!-- ========================================== -->
    <div class="form-group">
      <label class="required">نوع جایزه</label>
      <div class="radio-group">
        <label class="radio-label">
          <input type="radio" v-model="form.reward_type" value="percentage" />
          تخفیف درصدی
        </label>
        <label class="radio-label">
          <input type="radio" v-model="form.reward_type" value="fixed_amount" />
          تخفیف مبلغی
        </label>
        <label class="radio-label">
          <input type="radio" v-model="form.reward_type" value="free_service" />
          خدمت رایگان
        </label>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- 6. مقدار جایزه -->
    <!-- ========================================== -->
    <div class="form-group" v-if="form.reward_type !== 'free_service'">
      <label class="required">
        {{ form.reward_type === 'percentage' ? 'درصد تخفیف' : 'مبلغ تخفیف (تومان)' }}
      </label>
      <input
          v-model.number="form.reward_value"
          type="number"
          :min="form.reward_type === 'percentage' ? 1 : 1000"
          :max="form.reward_type === 'percentage' ? 100 : 999999999"
          :step="form.reward_type === 'percentage' ? 1 : 1000"
          class="form-control"
          required
      />
      <small class="help-text" v-if="form.reward_type === 'percentage'">
        ⚠️ درصد تخفیف باید بین 1 تا 100 باشد.
      </small>
      <small class="help-text" v-else>
        ⚠️ مبلغ تخفیف به تومان وارد می‌شود.
      </small>
    </div>

    <!-- ========================================== -->
    <!-- 7. سقف ریالی (فقط برای تخفیف درصدی) -->
    <!-- ========================================== -->
    <div class="form-group" v-if="form.reward_type === 'percentage'">
      <label>سقف ریالی تخفیف</label>
      <input
          v-model.number="form.max_discount_amount"
          type="number"
          min="0"
          step="1000"
          placeholder="اختیاری - مثلاً 50000"
          class="form-control"
      />
      <small class="help-text">
        ⚠️ اگر سقف تعیین نشود، تخفیف بدون محدودیت اعمال می‌شود.
        <br>
        مثال: ۲۰٪ تخفیف تا سقف ۵۰,۰۰۰ تومان
      </small>
    </div>

    <!-- ========================================== -->
    <!-- 8. خدمات مشمول تخفیف (تخفیف روی چه خدماتی اعمال می‌شود) -->
    <!-- ========================================== -->
    <div v-if="form.reward_type !== 'free_service'" class="form-group">
      <label class="required">خدمات مشمول تخفیف</label>
      <p class="field-description">
        تخفیف روی چه خدماتی اعمال می‌شود؟
      </p>
      <div class="radio-group">
        <label class="radio-label">
          <input type="radio" v-model="rewardType" value="all" />
          همه خدمات
          <span class="radio-desc">(تخفیف روی همه خدمات اعمال می‌شود)</span>
        </label>
        <label class="radio-label">
          <input type="radio" v-model="rewardType" value="specific" />
          فقط خدمات خاص
          <span class="radio-desc">(تخفیف فقط روی خدمات مشخص شده اعمال می‌شود)</span>
        </label>
      </div>

      <!-- انتخاب خدمات مشمول تخفیف -->
      <div v-if="rewardType === 'specific'" class="checkbox-group">
        <label
            v-for="service in services"
            :key="service.id"
            class="checkbox-label"
        >
          <input
              type="checkbox"
              :value="service.id"
              v-model="form.reward_service_ids"
          />
          {{ service.title }}
        </label>
        <div v-if="form.reward_service_ids.length === 0" class="warning-text">
          ⚠️ لطفاً حداقل یک خدمت را انتخاب کنید.
        </div>
      </div>

      <small class="help-text">
        <span v-if="rewardType === 'all'">
          ✅ تخفیف روی همه خدمات سالن اعمال می‌شود.
        </span>
        <span v-else>
          ✅ تخفیف فقط روی خدماتی که انتخاب می‌کنید اعمال می‌شود.
        </span>
      </small>
    </div>

    <!-- ========================================== -->
    <!-- 9. خدمت هدیه (فقط برای نوع free_service) -->
    <!-- ========================================== -->
    <div v-if="form.reward_type === 'free_service'" class="form-group">
      <label class="required">خدمت هدیه</label>
      <p class="field-description">
        چه خدمتی به عنوان هدیه به کاربر داده می‌شود؟
      </p>

      <div class="radio-group">
        <label class="radio-label">
          <input
              type="radio"
              v-model="giftType"
              value="specific"
          />
          یک خدمت مشخص
          <span class="radio-desc">(همیشه یک خدمت خاص به عنوان هدیه داده می‌شود)</span>
        </label>
        <label class="radio-label">
          <input
              type="radio"
              v-model="giftType"
              value="any"
          />
          انتخاب با کاربر
          <span class="radio-desc">(کاربر می‌تواند هر خدمتی را انتخاب کند)</span>
        </label>
      </div>

      <!-- انتخاب خدمت مشخص -->
      <div v-if="giftType === 'specific'" style="margin-top: 10px;">
        <select v-model="form.gift_service_id" class="form-control" required>
          <option :value="null">-- انتخاب کنید --</option>
          <option
              v-for="service in services"
              :key="service.id"
              :value="service.id"
          >
            {{ service.title }}
          </option>
        </select>
        <small class="help-text" v-if="form.gift_service_id">
          ✅ خدمت {{ getServiceName(form.gift_service_id) }} به عنوان هدیه داده می‌شود.
        </small>
        <small class="help-text" v-else>
          ⚠️ لطفاً یک خدمت را به عنوان هدیه انتخاب کنید.
        </small>
      </div>

      <div v-if="giftType === 'any'" style="margin-top: 10px;">
        <div class="info-box">
          ✅ کاربر می‌تواند هر خدمتی را که می‌خواهد به عنوان هدیه انتخاب کند.
          <br>
          <small>⚠️ محدودیتی برای انتخاب خدمت وجود ندارد.</small>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- 10. اعتبار کوپن -->
    <!-- ========================================== -->
    <div class="form-group">
      <label class="required">اعتبار کوپن (روز)</label>
      <input
          v-model.number="form.expire_days_after_receive"
          type="number"
          min="1"
          max="365"
          class="form-control"
          required
      />
      <small class="help-text">
        ✅ کوپن به مدت {{ form.expire_days_after_receive || '...' }} روز پس از دریافت اعتبار دارد.
        <br>
        مثال: اگر ۳۰ روز باشد، کاربر ۳۰ روز فرصت دارد از کوپن استفاده کند.
      </small>
    </div>

    <!-- ========================================== -->
    <!-- 11. تاریخ شروع و پایان -->
    <!-- ========================================== -->
    <div class="form-row">
      <div class="form-group half">
        <label class="required">تاریخ شروع</label>
        <date-picker
            v-model="form.start_date"
            type="date"
            :min="today"
            required
        />
        <small class="help-text">تاریخی که جشنواره از آن روز فعال می‌شود.</small>
      </div>
      <div class="form-group half">
        <label class="required">تاریخ پایان</label>
        <date-picker
            v-model="form.end_date"
            type="date"
            :min="form.start_date || today"
            required
        />
        <small class="help-text">تاریخی که جشنواره در آن روز به پایان می‌رسد.</small>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- 12. وضعیت فعال/غیرفعال -->
    <!-- ========================================== -->
    <div class="form-group">
      <label>وضعیت</label>
      <div class="radio-group">
        <label class="radio-label">
          <input type="radio" v-model="form.is_active" :value="1" />
          فعال
          <span class="radio-desc">(جشنواره در سیستم فعال است)</span>
        </label>
        <label class="radio-label">
          <input type="radio" v-model="form.is_active" :value="0" />
          غیرفعال
          <span class="radio-desc">(جشنواره غیرفعال است و به کاربران تعلق نمی‌گیرد)</span>
        </label>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- 13. نمایش خلاصه جشنواره -->
    <!-- ========================================== -->
    <div v-if="isFormValid" class="summary-box">
      <h4>📋 خلاصه جشنواره</h4>
      <div class="summary-item">
        <strong>شرط:</strong>
        <span v-if="form.type === 'first_visit'">بار اول مراجعه</span>
        <span v-else>
          {{ form.min_visits || '?' }} بار
          <span v-if="conditionType === 'all'">هر خدمتی</span>
          <span v-else>{{ getSelectedServicesText(form.condition_service_ids) }}</span>
          در {{ form.within_days || '?' }} روز
        </span>
      </div>
      <div class="summary-item">
        <strong>جایزه:</strong>
        <span v-if="form.reward_type === 'percentage'">
          {{ form.reward_value || '?' }}% تخفیف
          <span v-if="form.max_discount_amount">(تا سقف {{ formatPrice(form.max_discount_amount) }})</span>
          <span v-if="rewardType === 'all'"> روی همه خدمات</span>
          <span v-else> روی {{ getSelectedServicesText(form.reward_service_ids) }}</span>
        </span>
        <span v-else-if="form.reward_type === 'fixed_amount'">
          {{ formatPrice(form.reward_value) }} تخفیف
          <span v-if="rewardType === 'all'"> روی همه خدمات</span>
          <span v-else> روی {{ getSelectedServicesText(form.reward_service_ids) }}</span>
        </span>
        <span v-else-if="form.reward_type === 'free_service'">
          <span v-if="giftType === 'any'">یک خدمت رایگان (انتخاب با کاربر)</span>
          <span v-else-if="form.gift_service_id">یک نوبت {{ getServiceName(form.gift_service_id) }} رایگان</span>
          <span v-else>یک خدمت رایگان</span>
        </span>
      </div>
      <div class="summary-item">
        <strong>اعتبار کوپن:</strong>
        {{ form.expire_days_after_receive || '?' }} روز
      </div>
      <div class="summary-item">
        <strong>تاریخ:</strong>
        {{ formatDate(form.start_date) }} تا {{ formatDate(form.end_date) }}
      </div>
    </div>

    <!-- ========================================== -->
    <!-- 14. دکمه‌ها -->
    <!-- ========================================== -->
    <div class="form-actions">
      <button type="submit" class="btn-primary" :disabled="submitting || !isFormValid">
        <span v-if="submitting">⏳ در حال ذخیره...</span>
        <span v-else>{{ isEdit ? '✏️ ویرایش جشنواره' : '➕ ایجاد جشنواره' }}</span>
      </button>
      <button type="button" class="btn-secondary" @click="$emit('cancel')">
        ❌ انصراف
      </button>
    </div>

    <!-- ========================================== -->
    <!-- 15. نمایش خطاها -->
    <!-- ========================================== -->
    <div v-if="errors.length > 0" class="error-box">
      <strong>❌ خطاهای زیر را برطرف کنید:</strong>
      <ul>
        <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
      </ul>
    </div>
  </form>
</template>

<script>
import promotionApi from '@/api/promotion';

export default {
  name: 'PromotionForm',

  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    initialData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      submitting: false,
      services: [],
      errors: [],
      today: new Date().toISOString().split('T')[0],

      // نوع انتخاب خدمات مشمول شرط
      conditionType: 'all', // 'all' یا 'specific'

      // نوع انتخاب خدمات مشمول تخفیف
      rewardType: 'all', // 'all' یا 'specific'

      // نوع انتخاب خدمت هدیه
      giftType: 'specific', // 'specific' یا 'any'

      // فرم اصلی
      form: {
        title: '',
        type: 'first_visit',

        // بخش شرط
        condition_all_services: 1,
        condition_service_ids: [],

        // بخش جایزه
        reward_type: 'percentage',
        reward_value: null,
        max_discount_amount: null,

        // بخش خدمات مشمول تخفیف
        reward_all_services: 1,
        reward_service_ids: [],

        // بخش خدمت هدیه
        gift_service_id: null,
        gift_service_allowed_all: 0,

        // بخش وفاداری
        min_visits: null,
        within_days: null,

        // بخش اعتبار
        expire_days_after_receive: 30,
        start_date: null,
        end_date: null,
        is_active: 1
      }
    };
  },

  computed: {
    // بررسی اعتبار فرم
    isFormValid() {
      // عنوان
      if (!this.form.title || this.form.title.trim() === '') return false;

      // نوع جایزه
      if (!this.form.reward_type) return false;

      // مقدار جایزه (به جز خدمت رایگان)
      if (this.form.reward_type !== 'free_service') {
        if (!this.form.reward_value || this.form.reward_value <= 0) return false;
        if (this.form.reward_type === 'percentage' && this.form.reward_value > 100) return false;
      }

      // خدمت هدیه (اگر نوع جایزه free_service باشد)
      if (this.form.reward_type === 'free_service') {
        if (this.giftType === 'specific' && !this.form.gift_service_id) return false;
      }

      // خدمات مشمول تخفیف (اگر نوع جایزه free_service نباشد)
      if (this.form.reward_type !== 'free_service') {
        if (this.rewardType === 'specific' && this.form.reward_service_ids.length === 0) return false;
      }

      // شرایط وفاداری
      if (this.form.type === 'loyalty_count') {
        if (!this.form.min_visits || this.form.min_visits < 1) return false;
        if (!this.form.within_days || this.form.within_days < 1) return false;
      }

      // تاریخ‌ها
      if (!this.form.start_date) return false;
      if (!this.form.end_date) return false;
      if (new Date(this.form.start_date) > new Date(this.form.end_date)) return false;

      // خدمات مشمول شرط
      if (this.conditionType === 'specific' && this.form.condition_service_ids.length === 0) return false;

      return true;
    }
  },

  watch: {
    // وقتی کاربر نوع خدمات مشمول شرط را تغییر می‌دهد
    conditionType(val) {
      if (val === 'all') {
        this.form.condition_all_services = 1;
        this.form.condition_service_ids = [];
      } else {
        this.form.condition_all_services = 0;
      }
    },

    // وقتی کاربر نوع خدمات مشمول تخفیف را تغییر می‌دهد
    rewardType(val) {
      if (val === 'all') {
        this.form.reward_all_services = 1;
        this.form.reward_service_ids = [];
      } else {
        this.form.reward_all_services = 0;
      }
    },

    // وقتی کاربر نوع خدمت هدیه را تغییر می‌دهد
    giftType(val) {
      if (val === 'any') {
        this.form.gift_service_allowed_all = 1;
        this.form.gift_service_id = null;
      } else {
        this.form.gift_service_allowed_all = 0;
      }
    },

    // وقتی نوع جایزه تغییر می‌کند، مقدار را ریست کن
    'form.reward_type'(newVal) {
      if (newVal === 'free_service') {
        this.form.reward_value = null;
        this.form.max_discount_amount = null;
      }
    }
  },

  mounted() {
    this.loadServices();
    this.loadInitialData();
  },

  methods: {
    // ==============================
    // بارگذاری اطلاعات
    // ==============================

    async loadServices() {
      try {
        const { data } = await promotionApi.getServices();
        this.services = data.orders || [];
      } catch (error) {
        console.error('خطا در بارگذاری خدمات:', error);
        alert('خطا در بارگذاری لیست خدمات');
      }
    },

    loadInitialData() {
      if (this.isEdit && this.initialData && Object.keys(this.initialData).length > 0) {
        const data = this.initialData;

        // تنظیم فرم
        this.form = {
          title: data.title || '',
          type: data.type || 'first_visit',

          condition_all_services: data.condition_all_services ?? 1,
          condition_service_ids: [],

          reward_type: data.reward_type || 'percentage',
          reward_value: data.reward_value || null,
          max_discount_amount: data.max_discount_amount || null,

          reward_all_services: data.reward_all_services ?? 1,
          reward_service_ids: [],

          gift_service_id: data.gift_service_id || null,
          gift_service_allowed_all: data.gift_service_allowed_all || 0,

          min_visits: data.min_visits || null,
          within_days: data.within_days || null,

          expire_days_after_receive: data.expire_days_after_receive || 30,
          start_date: data.start_date || null,
          end_date: data.end_date || null,
          is_active: data.is_active ?? 1
        };

        // تنظیم conditionType
        this.conditionType = this.form.condition_all_services ? 'all' : 'specific';

        // تنظیم condition_service_ids
        if (data.condition_service_ids) {
          try {
            this.form.condition_service_ids = JSON.parse(data.condition_service_ids) || [];
          } catch {
            this.form.condition_service_ids = [];
          }
        }

        // تنظیم rewardType
        this.rewardType = this.form.reward_all_services ? 'all' : 'specific';

        // تنظیم reward_service_ids
        if (data.reward_service_ids) {
          try {
            this.form.reward_service_ids = JSON.parse(data.reward_service_ids) || [];
          } catch {
            this.form.reward_service_ids = [];
          }
        }

        // تنظیم giftType
        this.giftType = this.form.gift_service_allowed_all ? 'any' : 'specific';
      }
    },

    // ==============================
    // توابع کمکی
    // ==============================

    getServiceName(id) {
      const service = this.services.find(s => s.id === id);
      return service ? service.title : 'نامشخص';
    },

    getSelectedServicesText(ids) {
      if (!ids || ids.length === 0) return 'هیچ';
      const names = this.services
          .filter(s => ids.includes(s.id))
          .map(s => s.title);
      return names.length > 0 ? names.join('، ') : 'خدمات نامشخص';
    },

    formatPrice(value) {
      if (!value) return '۰';
      return new Intl.NumberFormat('fa-IR').format(value) + ' تومان';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fa-IR');
    },

    // ==============================
    // اعتبارسنجی
    // ==============================

    validateForm() {
      this.errors = [];

      // عنوان
      if (!this.form.title || this.form.title.trim() === '') {
        this.errors.push('عنوان جشنواره الزامی است');
      }

      // نوع جایزه و مقدار
      if (this.form.reward_type !== 'free_service') {
        if (!this.form.reward_value || this.form.reward_value <= 0) {
          this.errors.push('مقدار جایزه باید بیشتر از صفر باشد');
        }
        if (this.form.reward_type === 'percentage' && this.form.reward_value > 100) {
          this.errors.push('درصد تخفیف نمی‌تواند بیشتر از ۱۰۰ باشد');
        }
      }

      // خدمات مشمول تخفیف
      if (this.form.reward_type !== 'free_service') {
        if (this.rewardType === 'specific' && this.form.reward_service_ids.length === 0) {
          this.errors.push('حداقل یک خدمت برای تخفیف انتخاب کنید');
        }
      }

      // خدمت هدیه
      if (this.form.reward_type === 'free_service') {
        if (this.giftType === 'specific' && !this.form.gift_service_id) {
          this.errors.push('لطفاً یک خدمت را به عنوان هدیه انتخاب کنید');
        }
      }

      // شرایط وفاداری
      if (this.form.type === 'loyalty_count') {
        if (!this.form.min_visits || this.form.min_visits < 1) {
          this.errors.push('تعداد مراجعه باید حداقل ۱ باشد');
        }
        if (!this.form.within_days || this.form.within_days < 1) {
          this.errors.push('بازه زمانی باید حداقل ۱ روز باشد');
        }
      }

      // تاریخ‌ها
      if (!this.form.start_date) {
        this.errors.push('تاریخ شروع الزامی است');
      }
      if (!this.form.end_date) {
        this.errors.push('تاریخ پایان الزامی است');
      }
      if (this.form.start_date && this.form.end_date) {
        if (new Date(this.form.start_date) > new Date(this.form.end_date)) {
          this.errors.push('تاریخ شروع نمی‌تواند از تاریخ پایان بزرگتر باشد');
        }
      }

      // خدمات مشمول شرط
      if (this.conditionType === 'specific' && this.form.condition_service_ids.length === 0) {
        this.errors.push('حداقل یک خدمت برای شرط انتخاب کنید');
      }

      return this.errors.length === 0;
    },

    // ==============================
    // ارسال فرم
    // ==============================

    async submitForm() {
      // اعتبارسنجی
      if (!this.validateForm()) {
        // اسکرول به بالای صفحه برای نمایش خطاها
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      this.submitting = true;

      try {
        // آماده‌سازی داده‌ها برای ارسال
        const data = this.prepareData();

        let response;
        if (this.isEdit) {
          response = await promotionApi.updatePromotion(this.initialData.id, data);
        } else {
          response = await promotionApi.createPromotion(data);
        }

        alert(response.data.message || 'عملیات با موفقیت انجام شد');
        this.$emit('success');

      } catch (error) {
        const message = error.response?.data?.message || 'خطا در ذخیره اطلاعات';
        alert(message);
        console.error(error);
      } finally {
        this.submitting = false;
      }
    },

    // ==============================
    // آماده‌سازی داده‌ها برای ارسال
    // ==============================

    prepareData() {
      const data = { ...this.form };

      // تبدیل آرایه خدمات شرط به JSON
      if (data.condition_service_ids && data.condition_service_ids.length > 0) {
        data.condition_service_ids = JSON.stringify(data.condition_service_ids);
      } else {
        data.condition_service_ids = null;
      }

      // تبدیل آرایه خدمات تخفیف به JSON
      if (data.reward_service_ids && data.reward_service_ids.length > 0) {
        data.reward_service_ids = JSON.stringify(data.reward_service_ids);
      } else {
        data.reward_service_ids = null;
      }

      // اگر همه خدمات مشمول شرط هستند، condition_service_ids را null کن
      if (data.condition_all_services) {
        data.condition_service_ids = null;
      }

      // اگر همه خدمات مشمول تخفیف هستند، reward_service_ids را null کن
      if (data.reward_all_services) {
        data.reward_service_ids = null;
      }

      // اگر نوع جایزه free_service نیست، فیلدهای مربوط به هدیه را پاک کن
      if (data.reward_type !== 'free_service') {
        data.gift_service_id = null;
        data.gift_service_allowed_all = 0;
      }

      // اگر type first_visit است، فیلدهای وفاداری را پاک کن
      if (data.type === 'first_visit') {
        data.min_visits = null;
        data.within_days = null;
      }

      return data;
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های کلی فرم */
/* ========================================== */
.promotion-form {
  max-width: 900px;
  margin: 0 auto;
  padding: 10px 0;
}

/* ========================================== */
/* گروه‌های فرم */
/* ========================================== */
.form-group {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.form-group:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 6px;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.field-description {
  font-size: 13px;
  color: #909399;
  margin: 4px 0 12px 0;
}

/* ========================================== */
/* ورودی‌ها */
/* ========================================== */
.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.3s, box-shadow 0.3s;
  background: white;
  color: #2c3e50;
}

.form-control:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.form-control:disabled {
  background: #f5f7fa;
  cursor: not-allowed;
}

select.form-control {
  appearance: auto;
  cursor: pointer;
}

/* ========================================== */
/* ردیف‌های دو ستونه */
/* ========================================== */
.form-row {
  display: flex;
  gap: 20px;
}

.form-row .form-group {
  flex: 1;
}

.form-group.half {
  flex: 1;
}

/* ========================================== */
/* رادیو و چک‌باکس */
/* ========================================== */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background 0.2s;
  background: #f8f9fa;
}

.radio-label:hover {
  background: #e8f0fe;
}

.radio-label input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.radio-desc {
  font-size: 12px;
  color: #909399;
  margin-right: 8px;
}

/* ========================================== */
/* چک‌باکس‌های گروهی برای خدمات */
/* ========================================== */
.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  max-height: 200px;
  overflow-y: auto;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: normal;
  cursor: pointer;
  padding: 6px 14px;
  background: white;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  transition: all 0.2s;
  font-size: 13px;
}

.checkbox-label:hover {
  border-color: #409eff;
  background: #f0f7ff;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

/* ========================================== */
/* باکس‌های اطلاعاتی */
/* ========================================== */
.info-box {
  padding: 12px 16px;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 6px;
  color: #2c3e50;
  font-size: 14px;
  margin-top: 8px;
}

.info-box small {
  color: #606266;
}

.warning-text {
  color: #e6a23c;
  font-size: 13px;
  margin-top: 8px;
}

/* ========================================== */
/* باکس خلاصه */
/* ========================================== */
.summary-box {
  padding: 16px 20px;
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  border-radius: 6px;
  margin: 20px 0 10px 0;
}

.summary-box h4 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.summary-item {
  padding: 4px 0;
  font-size: 14px;
  color: #303133;
}

.summary-item strong {
  color: #2c3e50;
  margin-left: 6px;
}

/* ========================================== */
/* متن راهنما */
/* ========================================== */
.help-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

/* ========================================== */
/* دکمه‌ها */
/* ========================================== */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #e4e7ed;
}

.btn-primary {
  padding: 12px 32px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s;
  min-width: 180px;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.btn-primary:disabled {
  background: #a0cfff;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  padding: 12px 24px;
  background: #909399;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.3s;
}

.btn-secondary:hover {
  background: #a6a9ad;
}

/* ========================================== */
/* باکس خطاها */
/* ========================================== */
.error-box {
  margin-top: 20px;
  padding: 16px 20px;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 6px;
  color: #f56c6c;
}

.error-box strong {
  display: block;
  margin-bottom: 8px;
}

.error-box ul {
  margin: 0;
  padding-right: 20px;
}

.error-box ul li {
  margin-bottom: 4px;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .form-group.half {
    flex: none;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary, .btn-secondary {
    width: 100%;
    justify-content: center;
  }

  .checkbox-group {
    max-height: 150px;
  }
}
</style>